import React, { useRef, useState } from "react";
import { ImagePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { eyeEnrollPhotos } from "../lib/api";

const CDN = "https://cdn.jsdelivr.net/npm/@vladmandic/face-api@1.7.15";
let faPromise = null;
const loadFa = () => (faPromise ||= new Function("u", "return import(u)")(`${CDN}/dist/face-api.esm.js`).then(async (fa) => {
  await Promise.all([fa.nets.tinyFaceDetector.loadFromUri(`${CDN}/model`), fa.nets.faceLandmark68Net.loadFromUri(`${CDN}/model`),
    fa.nets.faceRecognitionNet.loadFromUri(`${CDN}/model`)]);
  return fa;
}).catch((e) => { faPromise = null; throw e; }));

const within = (p, ms) => Promise.race([p, new Promise((_, rej) => setTimeout(() => rej(new Error("timeout")), ms))]);

const toCanvas = (file) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => {
    const s = Math.min(1, 1280 / Math.max(img.width, img.height));
    const c = document.createElement("canvas");
    c.width = Math.round(img.width * s); c.height = Math.round(img.height * s);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    URL.revokeObjectURL(img.src); resolve(c);
  };
  img.onerror = () => reject(new Error("bad image"));
  img.src = URL.createObjectURL(file);
});

const largestDescriptor = async (fa, canvas) => {
  const r = await fa.detectAllFaces(canvas, new fa.TinyFaceDetectorOptions({ inputSize: 608, scoreThreshold: 0.4 })).withFaceLandmarks().withFaceDescriptors();
  const big = [...r].sort((a, b) => b.detection.box.width - a.detection.box.width)[0];
  return big ? Array.from(big.descriptor) : null;
};

export const EyePhotoEnroll = ({ userId, setFaces, ownerName: OWNER_NAME }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);

  const run = async (files) => {
    const list = [...(files || [])].slice(0, 6);
    if (list.length < 3) return toast.error("Pick 3 to 6 clear photos of your face");
    setResult(null);
    try {
      setBusy("Loading face models…");
      const fa = await within(loadFa(), 25000).catch(() => null);  // no WebGL/WASM -> TV camera still learns from the server side
      const images = [], descriptors = [];
      for (const [i, f] of list.entries()) {
        setBusy(`Reading photo ${i + 1} of ${list.length}…`);
        const c = await toCanvas(f);
        images.push(c.toDataURL("image/jpeg", 0.85));
        const d = fa ? await within(largestDescriptor(fa, c), 15000).catch(() => null) : null;
        if (d) descriptors.push(d);
      }
      if (!descriptors.length) toast.warning("This browser could not run the face model — teaching the TV camera only. Re-run on the /eye PC for /eye too.");
      setBusy("Teaching Nexus…");
      const r = await eyeEnrollPhotos(userId, OWNER_NAME, "owner", images, descriptors);
      setFaces(r.faces || []);
      setResult(r);
      toast.success(`Nexus now knows ${OWNER_NAME}: ${r.tv_samples} TV samples, ${r.eye_samples_added} /eye samples`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Photo enrolment failed");
    } finally {
      setBusy("");
      if (input.current) input.current.value = "";
    }
  };

  return (
    <div className="space-y-2" data-testid="eye-photo-enroll">
      <input ref={input} type="file" accept="image/*" multiple hidden onChange={(e) => run(e.target.files)} data-testid="eye-photo-input" />
      <button onClick={() => input.current?.click()} disabled={!!busy} data-testid="eye-photo-enroll-btn"
        className="w-full flex items-center justify-center gap-2 border border-[#00F0FF]/60 text-[#00F0FF] rounded-full py-3 font-black hover:bg-[#00F0FF]/10 disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} {busy || `Enrol from photos (${OWNER_NAME} · owner)`}
      </button>
      <p className="text-[11px] text-cyan-200/50 text-center">3–6 clear, well-lit photos: face straight on, slight left, slight right. Only you in each photo.</p>
      {result && (
        <div className="rounded-xl border border-emerald-400/40 bg-emerald-400/10 p-2 text-xs text-emerald-100" data-testid="eye-photo-result">
          {result.photos.map((p) => <p key={p.photo}>Photo {p.photo}: {p.ok ? "face learned" : p.why}</p>)}
          <p className="font-bold mt-1">TV camera: {result.tv_samples} samples · /eye: +{result.eye_samples_added}</p>
        </div>
      )}
    </div>
  );
};
